import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  Voucher,
  PaginatedVoucherResponse,
  VoucherFilterValues,
} from 'src/models/Management.interface';
import { apiUrls } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { getApiErrorMessage } from 'src/utils/errorHandler';

export const useGetVoucher = () => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchVouchers = async (
    page = 1,
    limit = 100,
    search = '',
    sortBy = 'createdAt',
    order = 'DESC',
    filters: VoucherFilterValues = {
      voucherTypeId: '',
      status: '',
      startDate: null,
      endDate: null,
    }
  ) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        offset: page,
        limit: limit,
        search: search,
        sortBy: sortBy,
        order: order,
      };

      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.voucherTypeId) {
        params.voucherTypeId = filters.voucherTypeId;
      }
      if (filters.startDate) {
        const date =
          filters.startDate instanceof Date
            ? filters.startDate
            : new Date(filters.startDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        params.startDate = `${year}-${month}-${day}`;
      }
      if (filters.endDate) {
        const date =
          filters.endDate instanceof Date
            ? filters.endDate
            : new Date(filters.endDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        params.endDate = `${year}-${month}-${day}`;
      }

      const response = await axiosTokenInstance.get<PaginatedVoucherResponse>(
        apiUrls.GET_VOUCHER,
        {
          params,
          withCredentials: true,
        }
      );

      setVouchers(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error: unknown) {
      const err = error as {
        response?: { status: number; data: { message: string } };
      };
      if (err.response?.status === 404) {
        console.error(err.response.data.message);
        setVouchers([]);
      } else {
        const errorMessage = await getApiErrorMessage(error);
        showSnackbar(errorMessage, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 400);
    }
  };

  const deleteVoucher = async (id: string) => {
    try {
      const response = await axiosTokenInstance.delete(apiUrls.DELETE_VOUCHER, {
        params: { id },
      });
      showSnackbar(response.data.message, 'success');
      fetchVouchers();
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  return {
    vouchers,
    loading,
    totalItems,
    totalPages,
    currentPage,
    fetchVouchers,
    deleteVoucher,
  };
};
